import { useState, useMemo } from 'react';
import {
  employeeInfoFields,
  profileFieldOptions,
  calendarSharingFields,
} from '../../../shared/shared-profile-field-filter';
import PanelSectionHeader, {
  SummaryPanelVariant,
} from '../../../shared/panel-section-header';
import { documentTypeOptions } from '../../../shared/google-document-types';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import FilterAlt from '@mui/icons-material/FilterAlt';

interface RulesSummaryIfSectionProps {
  conditionBlocks?: any[];
  variant?: SummaryPanelVariant;
}

const fieldNameMap: Record<string, string> = {
  // Drive filters
  fileType: 'File Type',
  fileSize: 'File Size',
  fileSharedToUserExternalToDomain: 'user external to the domain',
  fileSharedToUserInternalToDomain: 'user internal to the domain',
  fileIsSharedPublic: 'anyone with the link (public)',
  fileSharedToSpecificUser: 'specific user',
  fileIsSharedToDomain: 'domain',
  // Group filters
  groupName: 'Group Name',
  memberCount: 'Member Count',
  groupEmail: 'Group Email',
};

export default function RulesSummaryIfSection({
  conditionBlocks = [],
  variant = 'full',
}: RulesSummaryIfSectionProps) {
  const theme = useTheme();
  const isCompact = variant === 'compact';
  const [conditionsExpanded, setConditionsExpanded] = useState(false);

  const hasValidConditions = useMemo(() => {
    if (!conditionBlocks) return false;
    return (conditionBlocks || []).some(
      (block: any) => block.conditions && block.conditions.length > 0
    );
  }, [conditionBlocks]);

  const visibleConditionBlocks = useMemo(() => {
    if (!conditionBlocks) return [];
    return conditionsExpanded ? conditionBlocks : conditionBlocks.slice(0, 2);
  }, [conditionBlocks, conditionsExpanded]);

  const collapsedCount = useMemo(() => {
    if (!conditionBlocks) return 0;
    return Math.max(0, conditionBlocks.length - 2);
  }, [conditionBlocks]);

  const renderConditionRow = (condition: any) => {
    const { fieldName, operator, value } = condition;

    let fieldDisplay = fieldName;
    let valueDisplay = value || '[enter value]';

    if (fieldName === 'fileType') {
      fieldDisplay = 'File Type';
      const docType = documentTypeOptions.find((o) => o.value === value);
      valueDisplay = docType
        ? docType.displayValue
        : value || '[select file type]';
    } else if (fieldName === 'fileSize') {
      fieldDisplay = 'File Size';
      const unitStr = condition.unit || 'MB';
      if (operator === 'between' && condition.secondaryValue) {
        valueDisplay = `${value || '0'} and ${
          condition.secondaryValue
        } ${unitStr}`;
      } else {
        valueDisplay = `${value || '0'} ${unitStr}`;
      }
    } else if (fieldName === 'memberCount') {
      fieldDisplay = 'Member Count';
      if (
        (operator === 'between' || operator === 'isNotBetween') &&
        condition.secondaryValue
      ) {
        valueDisplay = `${value || '0'} and ${condition.secondaryValue}`;
      } else {
        valueDisplay = value || '0';
      }
    } else if (condition.fieldInfoType === 'fileSharing') {
      fieldDisplay = 'File Sharing';
      if (fieldName === 'fileSharedToUserExternalToDomain') {
        valueDisplay = 'user external to the domain';
      } else if (fieldName === 'fileSharedToUserInternalToDomain') {
        valueDisplay = 'user internal to the domain';
      } else if (fieldName === 'fileIsSharedPublic') {
        valueDisplay = 'anyone with the link (public)';
      } else if (fieldName === 'fileSharedToSpecificUser') {
        valueDisplay = value ? `specific user: ${value}` : 'specific user';
      } else if (fieldName === 'fileIsSharedToDomain') {
        valueDisplay = value ? `domain: ${value}` : 'domain';
      } else {
        valueDisplay = fieldName;
      }
    } else {
      if (!fieldDisplay) {
        fieldDisplay = '[select field]';
      } else {
        const empMatch = employeeInfoFields.find((f) => f.value === fieldName);
        if (empMatch) {
          fieldDisplay = empMatch.display;
        } else {
          const orgMatch = profileFieldOptions.find(
            (f) => f.value === fieldName
          );
          if (orgMatch) {
            fieldDisplay = orgMatch.display;
          } else if (fieldName.startsWith('customAttribute.')) {
            const parts = fieldName.split('.');
            fieldDisplay =
              parts.length >= 3
                ? `${parts[1].replace(/_/g, ' ')} - ${parts
                    .slice(2)
                    .join('.')
                    .replace(/_/g, ' ')}`
                : fieldName.replace(/_/g, ' ');
          } else {
            const calMatch = calendarSharingFields.find(
              (f) => f.value === fieldName
            );
            if (calMatch) {
              fieldDisplay = calMatch.display;
            } else {
              const staticDisplay = fieldNameMap[fieldName];
              if (staticDisplay) {
                fieldDisplay = staticDisplay;
              }
            }
          }
        }
      }
    }

    const operatorLabels: Record<string, string> = {
      contains: 'contains',
      equals: 'equals',
      notContains: 'does not contain',
      notEquals: 'does not equal',
      startsWith: 'starts with',
      endsWith: 'ends with',
      larger: 'is greater than',
      smaller: 'is less than',
      greaterThan: 'is greater than',
      lessThan: 'is less than',
      greaterThanOrEqual: 'is greater than or equal to',
      lessThanOrEqual: 'is less than or equal to',
      between: 'is between',
      isNotBetween: 'is not between',
      isEmpty: 'is empty',
    };
    const operatorDisplay = operatorLabels[operator] || operator || 'contains';

    if (variant === 'compact') {
      return (
        <Stack
          direction="row"
          justifyContent="space-between"
          alignItems="center"
          spacing={1}
          sx={{ width: '100%' }}
        >
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.semantic.text.body,
              flexShrink: 0,
            }}
          >
            {fieldDisplay}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: theme.palette.semantic.text.title2,
              fontWeight: 500,
              textAlign: 'right',
            }}
          >
            <Box
              component="span"
              sx={{
                fontWeight: 300,
                color: theme.palette.semantic.text.title2,
                mr: 0.5,
              }}
            >
              {operatorDisplay}
            </Box>{' '}
            {valueDisplay}
          </Typography>
        </Stack>
      );
    }

    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          width: '100%',
          gap: 1.5,
        }}
      >
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.semantic.text.body,
            flex: 1,
            minWidth: 0,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={fieldDisplay}
        >
          {fieldDisplay}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            fontWeight: 300,
            color: theme.palette.semantic.text.title2,
            width: 110,
            flexShrink: 0,
            textAlign: 'left',
          }}
        >
          {operatorDisplay}
        </Typography>
        <Typography
          variant="body2"
          sx={{
            color: theme.palette.semantic.text.title2,
            fontWeight: 500,
            width: 70,
            flexShrink: 0,
            textAlign: 'right',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
          title={valueDisplay}
        >
          {valueDisplay}
        </Typography>
      </Box>
    );
  };

  const renderBlockLabel = (block: any) => {
    if (!block.conditions || block.conditions.length === 0) return '-';
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 0.5,
          width: '100%',
        }}
      >
        {block.conditions.map((cond: any, index: number) => {
          const condRow = renderConditionRow(cond);
          if (!condRow) return null;

          if (index === 0) return <Box key={cond.id || index}>{condRow}</Box>;

          const logicalOp =
            block.conditions[index - 1]?.logicalOperator || 'AND';
          return (
            <Box key={cond.id || index}>
              <Typography
                variant="caption"
                sx={{
                  color: theme.palette.semantic.text.body,
                  fontWeight: 300,
                  display: 'block',
                  textAlign: 'center',
                  textTransform: 'lowercase',
                  my: 0.25,
                }}
              >
                {logicalOp}
              </Typography>
              {condRow}
            </Box>
          );
        })}
      </Box>
    );
  };

  const renderConditionBlocks = () => {
    return visibleConditionBlocks.map((block: any, blockIndex: number) => {
      const blockLabel = renderBlockLabel(block);
      if (!blockLabel) return null;
      return (
        <Box
          key={block.id || blockIndex}
          sx={{
            width: '100%',
          }}
        >
          {blockIndex > 0 && (
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: 'center',
                color: theme.palette.semantic.text.title2,
                mb: 1.5,
                mt: 0.5,
              }}
            >
              Or
            </Typography>
          )}
          <Box
            sx={{
              border: '0.5px solid',
              borderColor: theme.palette.semantic.outline.light,
              borderRadius: variant === 'compact' ? 1 : 2,
              p: variant === 'compact' ? 1.5 : 2,
              backgroundColor: theme.palette.semantic.surface.bg1,
            }}
          >
            {blockLabel}
          </Box>
        </Box>
      );
    });
  };

  return (
    <Box
      sx={{
        mt: 2,
        spacing: 1,
      }}
    >
      <PanelSectionHeader variant={variant} title="IF" icon={FilterAlt} />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 1.5,
          ...(!isCompact && {
            backgroundColor: theme.palette.semantic.surface.bg3,
            p: 2,
            border: '1px solid',
            borderColor: theme.palette.semantic.outline.light,
          }),
        }}
      >
        {hasValidConditions ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              px: 0.5,
            }}
          >
            {renderConditionBlocks()}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', justifyContent: 'flex-start', px: 1 }}>
            <Typography variant="body2">—</Typography>
          </Box>
        )}
        {collapsedCount > 0 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
            <Button
              variant="text"
              size="small"
              onClick={() => setConditionsExpanded(!conditionsExpanded)}
              startIcon={conditionsExpanded ? <ExpandLess /> : <ExpandMore />}
              sx={{
                textTransform: 'none',
                fontSize: '0.75rem',
                fontWeight: 500,
                p: 0.5,
                color: theme.palette.semantic.text.body,
                '&:hover': {
                  bgcolor: 'transparent',
                  textDecoration: 'underline',
                },
              }}
            >
              {conditionsExpanded
                ? 'Show less'
                : `Show ${collapsedCount} more condition${
                    collapsedCount > 1 ? 's' : ''
                  }`}
            </Button>
          </Box>
        )}
      </Box>
    </Box>
  );
}
